import React, { useCallback } from 'react';

import { PriceLabel } from 'src/components/offer/price/price-label';
import Rating from 'src/components/rating';
import { CountTranslation } from 'src/components/rating/types';

import * as S from './styles';
import { IconShoppingCart } from '@prisjakt/ui/icon';

type ProductRowProps = {
  name: string;
  link?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  image?: React.ReactNode;
  rating?: number;
  ratingCount?: number;
  ratingCountTranslation?: CountTranslation;
  subtitle?: React.ReactNode;
  campaignBadge?: React.ReactNode;
  price?: React.ReactNode;
  linkAttributes?: { target: string; rel: string };
  storesCount?: number;
  storesCountText?: string;
  className?: string;
  showDivider?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onDeselect?: () => void;
  children?: React.ReactNode;
};

const ProductRow = ({
  onClick,
  linkAttributes,
  link = '',
  image,
  name,
  rating,
  subtitle,
  campaignBadge,
  ratingCount,
  ratingCountTranslation = {
    domain: 'product',
    key: 'product-card.n-ratings',
  },
  price,
  storesCount,
  storesCountText,
  children,
  className,
  showDivider,
  selectable,
  selected,
  onSelect,
  onDeselect,
}: ProductRowProps) => {
  const onToggle = selected ? onDeselect : onSelect;
  const clickHandler = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (selectable) {
        e.preventDefault();
        onToggle?.();
        return;
      }

      onClick?.(e);
    },
    [selectable, onToggle, onClick]
  );
  return (
    <S.Row
      className={className}
      showDivider={showDivider}
      selectable={selectable}
      selected={selected}
    >
      <S.ProductLink
        onClick={clickHandler}
        {...(linkAttributes || {})}
        to={link}
        aria-label={name}
      >
        {image && <S.ProductImage>{image}</S.ProductImage>}

        <S.BasicInfo>
          <S.ProductName>{name}</S.ProductName>

          {rating ? (
            <Rating
              hide={rating === null}
              rate={rating}
              showRate
              size="small"
              count={ratingCount}
              countTranslation={ratingCountTranslation}
              showCounter
            />
          ) : null}

          {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
        </S.BasicInfo>

        {campaignBadge && <S.CampaignBadge>{campaignBadge}</S.CampaignBadge>}

        {price && (
          <S.PriceLabelWrapper>
            <PriceLabel value={price} />
            {storesCount !== null && (
              <S.StoreCount title={storesCountText}>
                <span>{storesCount}</span> <IconShoppingCart color="base500" />
              </S.StoreCount>
            )}
          </S.PriceLabelWrapper>
        )}
      </S.ProductLink>
      {children && <S.Gutter>{children}</S.Gutter>}
      {selectable && (
        <S.SelectBox
          selected={selected}
          onSelect={onSelect}
          onDeselect={onDeselect}
          aria-label={name}
        />
      )}
    </S.Row>
  );
};

export default ProductRow;
